import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Search } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { api, errorMessage } from "../../lib/api";
import { ALL_TEXT_KEYS, defaultText, useLang } from "../../lib/i18n";

const Cell = ({ id, lang, value, placeholder, onChange }) => (
  <Input dir={lang === "ar" ? "rtl" : "ltr"} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
    data-testid={id} className={`h-11 rounded-xl text-sm ${lang === "ar" ? "font-arabic text-right" : "text-left"} ${value ? "border-slate-900" : ""}`} />
);

export const TranslationsPanel = () => {
  const { t, overrides, setOverrides } = useLang();
  const [draft, setDraft] = useState({ en: { ...overrides.en }, ar: { ...overrides.ar } });
  const [query, setQuery] = useState("");
  const [onlyEdited, setOnlyEdited] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (lang, key) => (v) => setDraft((d) => ({ ...d, [lang]: { ...d[lang], [key]: v } }));

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return ALL_TEXT_KEYS.filter((k) => !onlyEdited || draft.en[k] || draft.ar[k]).filter((k) => !q || [defaultText("en", k), defaultText("ar", k),
      draft.en[k], draft.ar[k]].some((v) => (v || "").toLowerCase().includes(q)));
  }, [query, onlyEdited, draft]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/translations", draft);
      setOverrides(data);
      setDraft({ en: { ...data.en }, ar: { ...data.ar } });
      toast.success(t("Translations saved"));
    } catch (e) { toast.error(errorMessage(e)); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="translations-panel">
      <div className="flex flex-wrap items-end gap-4">
        <p className="me-auto max-w-xl text-sm text-slate-500">{t("Correct any text in the app. Leave a box empty to use the default text.")}</p>
        <button onClick={save} disabled={saving} data-testid="translations-save-button"
          className="btn-press min-h-[50px] rounded-2xl bg-slate-900 px-6 font-semibold text-white disabled:opacity-60">
          {saving ? t("Saving…") : t("Save translations")}
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative w-full max-w-sm">
          <Search className="absolute start-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("Search text")} data-testid="translations-search"
            className="h-12 w-full rounded-2xl border border-slate-200 bg-white pe-4 ps-11 text-sm outline-none focus:border-slate-900" />
        </div>
        <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
          <Switch checked={onlyEdited} onCheckedChange={setOnlyEdited} data-testid="translations-only-edited" /> {t("Show only edited")}
        </label>
        <span className="text-xs font-semibold text-slate-400" data-testid="translations-count">{t("{count} texts", { count: rows.length })}</span>
      </div>
      <div className="overflow-x-auto rounded-3xl border border-slate-200/80 bg-white">
        <table className="w-full min-w-[720px] table-fixed text-start">
          <thead>
            <tr className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
              <th className="px-5 py-3 text-start">{t("English")}</th><th className="px-5 py-3 text-start">{t("Arabic")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((k, i) => (
              <tr key={k} className="border-t border-slate-100 align-top" data-testid={`translation-row-${i}`}>
                <td className="px-5 py-3"><Cell id={`translation-en-${i}`} lang="en" value={draft.en[k] || ""} placeholder={defaultText("en", k) || k} onChange={set("en", k)} /></td>
                <td className="px-5 py-3"><Cell id={`translation-ar-${i}`} lang="ar" value={draft.ar[k] || ""} placeholder={defaultText("ar", k)} onChange={set("ar", k)} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
